/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { Code } from '@atlaskit/code';
import { cssMap, jsx } from '@atlaskit/css';
import { Text } from '@atlaskit/primitives/compiled';
import { Table, TBody, TD, TH, THead, TR } from '@atlaskit/table/primitives';
import { token } from '@atlaskit/tokens';

import HorizontalScrollContainer from '../horizontal-scroll-container';

const values: RadiusValue[] = [
	{
		tokenName: 'radius.xsmall',
		suitableFor: 'Small detail elements: badges, checkboxes, avatar labels, keyboard shortcuts.',
		value: '2px',
		focusTokenName: 'radius.focus.xsmall',
		focusRingRadiusValue: '4px (xsmall +2 offset)',
	},
	{
		tokenName: 'radius.small',
		suitableFor:
			'Supporting elements: labels, lozenges, timestamps, tags, dates, tooltip containers, imagery inside a table, compact buttons.',
		value: '4px',
		focusTokenName: 'radius.focus.small',
		focusRingRadiusValue: '6px (small +2 offset)',
	},
	{
		tokenName: 'radius.medium',
		suitableFor:
			'Interactive elements: buttons, inputs, text areas, selects, navigation items, smart links.',
		value: '6px',
		focusTokenName: 'radius.focus.medium',
		focusRingRadiusValue: '8px (medium +2 offset)',
	},
	{
		tokenName: 'radius.large',
		suitableFor: 'Containment elements: cards, in-page containers, floating UI, dropdown menus.',
		value: '8px',
		focusTokenName: 'radius.focus.large',
		focusRingRadiusValue: '10px (large +2 offset)',
	},
	{
		tokenName: 'radius.xlarge',
		suitableFor:
			'Large page elements: full-page containers, large containers, modals, Kanban columns, tables.',
		value: '12px',
		focusTokenName: 'radius.focus.xlarge',
		focusRingRadiusValue: '14px (xlarge +2 offset)',
	},
	{
		tokenName: 'radius.xxlarge',
		suitableFor: 'Video player containers.',
		value: '16px',
		focusTokenName: 'radius.focus.xxlarge',
		focusRingRadiusValue: '18px (xxlarge +2 offset)',
	},
	{
		tokenName: 'radius.full',
		suitableFor:
			'Circular elements (user/people related): avatars, names, user-related UI, emoji reactions.',
		value: '999px',
	},
	{
		tokenName: 'radius.tile',
		suitableFor: 'Use this specific radius token exclusively for the tile component system.',
		value: 'Calculated 25%',
	},
];

type RadiusValue = {
	tokenName: string;
	suitableFor: string;
	value: string;
	focusTokenName?: string;
	focusRingRadiusValue?: string;
};

/**
 * __Radius table__
 */
export const RadiusTable = (): React.JSX.Element => {
	return (
		<HorizontalScrollContainer>
			<Table testId="radius-table">
				<THead>
					<TR isBodyRow={false}>
						<TH width="110px">Token name</TH>
						<TH width="430px">Suitable for</TH>
						<TH width="90px">Value*</TH>
						<TH width="70px">Visual preview</TH>
					</TR>
				</THead>
				<TBody>
					{values.map((token) => {
						return (
							<TR key={token.tokenName}>
								<TD>
									<Code>{token.tokenName}</Code>
								</TD>
								<TD>
									<Text>{token.suitableFor}</Text>
								</TD>
								<TD>
									<Text>{token.value}</Text>
								</TD>
								<TD>
									{token.tokenName !== 'radius.tile' && (
										<RadiusPreview size={token.tokenName.replace('radius.', '') as RadiusSize} />
									)}
								</TD>
							</TR>
						);
					})}
				</TBody>
			</Table>
		</HorizontalScrollContainer>
	);
};

type RadiusSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'full';

const styles = cssMap({
	base: {
		backgroundColor: token('color.background.neutral'),
		width: '45px',
		height: '45px',
		position: 'relative',
		'&::after': {
			content: '',
			position: 'absolute',
			// @ts-expect-error - need to position the element outside the original
			insetBlockEnd: '-1px',
			// @ts-expect-error - need to position the element outside the original
			insetInlineStart: '-1px',
			width: '50%',
			height: '50%',
			borderInlineStartWidth: token('border.width.focused'),
			borderBlockEndWidth: token('border.width.focused'),
			borderBlockStartWidth: '0',
			borderInlineEndWidth: '0',
			borderColor: token('color.border.accent.magenta'),
			borderStyle: 'solid',
		},
	},
	xsmall: {
		borderRadius: token('radius.xsmall'),
		'&::after': {
			borderEndStartRadius: token('radius.xsmall'),
		},
	},
	small: {
		borderRadius: token('radius.small'),
		'&::after': {
			borderEndStartRadius: token('radius.small'),
		},
	},
	medium: {
		borderRadius: token('radius.medium'),
		'&::after': {
			borderEndStartRadius: token('radius.medium'),
		},
	},
	large: {
		borderRadius: token('radius.large'),
		'&::after': {
			borderEndStartRadius: token('radius.large'),
		},
	},
	xlarge: {
		borderRadius: token('radius.xlarge'),
		'&::after': {
			borderEndStartRadius: token('radius.xlarge'),
		},
	},
	xxlarge: {
		borderRadius: token('radius.xxlarge'),
		'&::after': {
			borderEndStartRadius: token('radius.xxlarge'),
		},
	},
	full: {
		borderRadius: token('radius.full'),
		'&::after': {
			borderEndStartRadius: token('radius.full'),
		},
	},
});

const RadiusPreview = ({ size }: { size: RadiusSize }) => {
	return <div css={[styles.base, styles[size as RadiusSize]]}></div>;
};

/**
 * __Radius focus table__
 */
export const RadiusFocusTable = (): React.JSX.Element => {
	return (
		<HorizontalScrollContainer>
			<Table testId="radius-focus-table">
				<THead>
					<TR isBodyRow={false}>
						<TH width="150px">Element radius token</TH>
						<TH width="150px">Focus radius token to pair with</TH>
						<TH width="150px">Focus ring radius value*</TH>
					</TR>
				</THead>
				<TBody>
					{values.map((token) => {
						return (
							!['radius.full', 'radius.tile'].includes(token.tokenName) && (
								<TR key={token.tokenName}>
									<TD>
										<Code>{token.tokenName}</Code>
									</TD>
									<TD>
										<Code>{token.focusTokenName}</Code>
									</TD>
									<TD>
										<Text>{token.focusRingRadiusValue}</Text>
									</TD>
								</TR>
							)
						);
					})}
				</TBody>
			</Table>
		</HorizontalScrollContainer>
	);
};
